<template>
    <div class="mt-3">
        <h6><i class="fas fa-paperclip"></i> Прикрепленные файлы:</h6>

        <div v-if="requiredAttachmentRules.length" class="border rounded p-3 mb-3 bg-light">
            <h6 class="mb-3">
                <i class="fas fa-clipboard-check"></i>
                Обязательные документы
            </h6>
            <div
                v-for="rule in requiredAttachmentRules"
                :key="rule.key"
                class="required-attachment-item mb-3"
            >
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                    <div class="font-weight-bold">
                        {{ rule.label }}
                        <b-badge
                            :variant="isRequiredAttachmentSatisfied(rule) ? 'success' : 'warning'"
                            class="ml-2"
                        >
                            {{ getFilesByKind(rule.key).length }}/{{ rule.minCount }}
                        </b-badge>
                    </div>
                    <small class="text-muted">{{ getRequiredForText(rule) }} · {{ getUploadRolesText(rule) }}</small>
                </div>

                <div
                    v-if="canUploadRequiredAttachment(rule)"
                    class="d-flex align-items-center gap-2 mt-2"
                >
                    <b-form-file
                        :value="requiredAttachmentInputs[rule.key] || null"
                        multiple
                        class="flex-grow-1"
                        placeholder="Выберите файлы или перетащите их сюда"
                        drop-placeholder="Перетащите файлы сюда..."
                        browse-text="Обзор"
                        :disabled="isAnyActionLoading"
                        @input="files => uploadRequiredAttachment(rule, files)"
                    />
                    <b-button
                        type="button"
                        variant="outline-secondary"
                        :disabled="isAnyActionLoading"
                        @click="pasteFromClipboardToRequiredAttachment(rule)"
                        v-b-tooltip.hover
                        title="Вставить файл из буфера обмена"
                        class="clipboard-paste-button"
                    >
                        <i class="fas fa-paste mr-1"></i>
                        <span class="d-none d-md-inline">Вставить из буфера обмена</span>
                    </b-button>
                </div>

                <b-alert v-if="requiredAttachmentErrors[rule.key]" show variant="danger" class="mt-2 mb-2">
                    {{ requiredAttachmentErrors[rule.key] }}
                    <div v-if="pendingRequiredAttachments[rule.key] && pendingRequiredAttachments[rule.key].length">
                        <div class="small text-break">Остались: {{ pendingRequiredAttachments[rule.key].map(file => file.name).join(', ') }}</div>
                        <b-button size="sm" variant="outline-danger" :disabled="isAnyActionLoading"
                                  @click="uploadRequiredAttachment(rule, pendingRequiredAttachments[rule.key])">
                            Повторить загрузку
                        </b-button>
                    </div>
                </b-alert>
                <b-alert v-else-if="requiredAttachmentResults[rule.key]" show variant="success" class="mt-2 mb-2">
                    {{ requiredAttachmentResults[rule.key] }}
                </b-alert>

                <div v-if="getFilesByKind(rule.key).length" class="mt-2">
                    <FileList
                        :list="getFilesByKind(rule.key)"
                        :can-edit="canUploadRequiredAttachment(rule) && !isAnyActionLoading"
                        :request-id="requestId"
                        @remove="removeRequestFile"
                    />
                </div>
            </div>
        </div>
        
        <!-- Форма загрузки новых файлов -->
        <div v-if="isSubmissionExtension && canUploadExtensionFiles" class="mb-3">
            <MechanicAssetFileUploadPanel
                v-model="extensionAttachments"
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,.txt"
                :disabled="uploadingExtensionFiles"
            />
            <b-button v-if="extensionAttachments.length" size="sm" variant="outline-primary"
                      :disabled="uploadingExtensionFiles" @click="uploadExtensionFiles">
                <b-spinner v-if="uploadingExtensionFiles" small class="mr-1" />
                Прикрепить файлы
            </b-button>
            <small class="text-muted d-block mt-1">Файлы можно добавить до первого согласования.</small>
        </div>
        <div v-else-if="!isSubmissionExtension && canEditRequestDetails && regularAttachmentsEnabled" class="mb-3">
            <MechanicAssetFileUploadPanel
                :value="newAttachments"
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,.txt"
                @input="setNewAttachments"
            />
            <small class="text-muted d-block mt-1">
                <i class="fas fa-info-circle mr-1"></i>
                Файлы будут загружены только после сохранения заявки. Можно выбрать файлы, вставить из буфера или сделать несколько фото подряд.
            </small>
        </div>

        <!-- Существующие обычные файлы -->
        <div v-if="regularRequestFiles && regularRequestFiles.length">
            <FileList
                :list="regularRequestFiles"
                :can-edit="!isSubmissionExtension && canEditRequestDetails"
                :request-id="requestId"
                @remove="removeRequestFile" 
            />
        </div>
        <p v-else-if="!newAttachments.length && !requiredAttachmentRules.length" class="text-muted mb-0">
            Файлы не прикреплены
        </p>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import FileList from '../FileList/FileList.vue';
import clipboardFileMixin from '@/mixins/clipboardFileMixin.js';
import MechanicAssetFileUploadPanel from '@/views/MechanicAssets/MechanicAssetFileUploadPanel.vue';
import requestApi from '@/store/Requests/requestCard/api';
import extensionProfile from '../../../../../../server/utils/Requests/timesheetExtensionProfile';
import {
    normalizeRequiredAttachmentRule as normalizeRequiredAttachmentRuleConfig,
    isRequiredAttachmentRuleEnabledByWorkflowData,
    getRequiredAttachmentTargetsText,
    getRequiredAttachmentUploadRoles,
    getRequiredAttachmentUploadRolesText
} from '@/utils/requestRequiredAttachments';

/**
 * Автономный компонент файлов заявки
 * Управляет загрузкой новых и отображением существующих файлов
 */
export default {
    name: 'RequestFiles',
    mixins: [clipboardFileMixin],
    components: {
        FileList,
        MechanicAssetFileUploadPanel
    },
    data() {
        return {
            requiredAttachmentInputs: {},
            pendingRequiredAttachments: {},
            requiredAttachmentErrors: {},
            requiredAttachmentResults: {},
            uploadingRequiredAttachmentKey: null,
            extensionAttachments: [],
            uploadingExtensionFiles: false
        };
    },
    computed: {
        ...mapState('requestCard', [
            'request',
            'newAttachments',
            'adminGodModeEnabled'
        ]),
        ...mapGetters('requestCard', [
            'canEditRequestDetails',
            'isAnyActionLoading'
        ]),
        requestId() {
            return this.request?.id;
        },
        isSubmissionExtension() {
            return extensionProfile.isExtension(this.request && this.request.type_category);
        },
        canUploadExtensionFiles() {
            const user = this.$store.getters.currentUser || {};
            return extensionProfile.canUploadFiles(this.request, this.request.Participants || [], user.userId || user.user_id);
        },
        requestFiles() {
            return this.request?.files || [];
        },
        regularRequestFiles() {
            return this.requestFiles.filter(file => !file.file_kind);
        },
        regularAttachmentsEnabled() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : {};
            return requestType.allow_attachments !== false;
        },
        requiredAttachmentRules() {
            if (this.isSubmissionExtension) return [];
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const config = workflowConfig.requiredAttachments || workflowConfig.required_attachments || {};
            if (config.enabled !== true || !Array.isArray(config.rules)) {
                return [];
            }

            return config.rules
                .map((rule, index) => this.normalizeRequiredAttachmentRule(rule, index))
                .filter(rule => rule.active && this.isRequiredAttachmentRuleEnabledForRequest(rule));
        }
    },
    methods: {
        ...mapActions('requestCard', [
            'requestCard_removeRequestFile',
            'requestCard_setNewAttachments',
            'requestCard_uploadRequiredAttachment'
        ]),

        async uploadExtensionFiles() {
            if (!this.canUploadExtensionFiles || this.uploadingExtensionFiles) return;
            this.uploadingExtensionFiles = true;
            try {
                for (const file of this.extensionAttachments.slice()) {
                    await requestApi.uploadRequestFiles(this.requestId, [file]);
                    this.extensionAttachments = this.extensionAttachments.filter(item => item !== file);
                }
            } catch (error) {
                const message = error.response && error.response.data && error.response.data.message;
                this.$bvToast.toast(message || 'Не удалось прикрепить файлы', {variant: 'danger', solid: true});
            } finally {
                this.uploadingExtensionFiles = false;
                this.$emit('saved');
            }
        },

        normalizeRequiredAttachmentRule(rule, index) {
            return normalizeRequiredAttachmentRuleConfig(rule, index);
        },

        isRequiredAttachmentRuleEnabledForRequest(rule) {
            return isRequiredAttachmentRuleEnabledByWorkflowData(
                rule,
                this.request && this.request.workflow_data ? this.request.workflow_data : {}
            );
        },

        getFilesByKind(fileKind) {
            return this.requestFiles.filter(file => file.file_kind === fileKind);
        },

        isRequiredAttachmentSatisfied(rule) {
            return this.getFilesByKind(rule.key).length >= rule.minCount;
        },

        getRequiredForText(rule) {
            return getRequiredAttachmentTargetsText(rule);
        },
        getUploadRoles(rule) {
            return getRequiredAttachmentUploadRoles(rule);
        },
        getUploadRolesText(rule) {
            return `прикладывает: ${getRequiredAttachmentUploadRolesText(rule)}`;
        },
        getCurrentUserParticipants() {
            const currentUser = this.$store.getters.currentUser || {};
            const currentUserId = Number(currentUser.userId || currentUser.user_id);
            if (!currentUserId) {
                return [];
            }
            return (this.request && Array.isArray(this.request.Participants) ? this.request.Participants : [])
                .filter(participant => Number(participant.user_id) === currentUserId);
        },
        userHasUploadRole(rule) {
            const roles = this.getUploadRoles(rule);
            const participants = this.getCurrentUserParticipants();
            if (roles.includes('all')) {
                return participants.length > 0;
            }
            return participants.some(participant =>
                roles.includes(String(participant.role || '').trim().toLowerCase())
            );
        },

        canUploadRequiredAttachment(rule) {
            if (!this.request || !rule) {
                return false;
            }
            if (this.request.isAdmin === true && this.adminGodModeEnabled === true) {
                return true;
            }
            if (['completed', 'rejected', 'cancelled'].includes(this.request.status)) {
                return false;
            }
            return this.userHasUploadRole(rule);
        },

        toFileArray(files) {
            if (Array.isArray(files)) {
                return files;
            }
            if (files && typeof files.length === 'number') {
                return Array.from(files);
            }
            return files ? [files] : [];
        },

        async uploadRequiredAttachment(rule, files) {
            const list = this.toFileArray(files);
            if (!list.length || this.isAnyActionLoading) return;

            this.$set(this.pendingRequiredAttachments, rule.key, list.slice());
            this.$set(this.requiredAttachmentInputs, rule.key, list);
            this.$set(this.requiredAttachmentErrors, rule.key, '');
            this.$set(this.requiredAttachmentResults, rule.key, '');
            this.uploadingRequiredAttachmentKey = rule.key;
            try {
                await this.requestCard_uploadRequiredAttachment({
                    fileKind: rule.key,
                    files: list,
                    onUploaded: file => {
                        this.$set(this.pendingRequiredAttachments, rule.key,
                            this.pendingRequiredAttachments[rule.key].filter(item => item !== file));
                    }
                });
                this.$set(this.requiredAttachmentResults, rule.key,
                    'Документ загружен. Удаление старых файлов и другие правки сохранятся при сохранении или отправке заявки.');
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : error.message || 'Не удалось загрузить документ';
                this.$set(this.requiredAttachmentErrors, rule.key, errorMsg);
            } finally {
                this.uploadingRequiredAttachmentKey = null;
                // Сброс после обновления input позволяет снова выбрать тот же файл.
                this.$nextTick(() => this.$set(this.requiredAttachmentInputs, rule.key, null));
            }
        },

        async pasteFromClipboardToRequiredAttachment(rule) {
            await this.handleClipboardButton((files) => {
                this.uploadRequiredAttachment(rule, files);
            });
        },

        setNewAttachments(files) {
            this.requestCard_setNewAttachments(files);
        },

        removeRequestFile(fileId) {
            this.requestCard_removeRequestFile(fileId);
        }
    }
};
</script>

<style scoped>
.required-attachment-item:last-child {
    margin-bottom: 0 !important;
}

.gap-2 {
    gap: 0.5rem;
}
</style>
